import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import LandingPageForm from './LandingPageForm';
import VideoModal from './VideoModal';
import LandingPageAnalytics from './LandingPageAnalytics';
import LandingPageHeader from './LandingPageHeader';
import LandingPageEmptyState from './LandingPageEmptyState';
import LandingPageListItems from './LandingPageListItems';
import LandingPagePagination from './LandingPagePagination';
// Modal components handled by parent App.js
// DashboardLayout import removed - will be handled by parent

const LandingPageList = ({ projectId, onBack, user, onShowFeedbackModal, onUpgradeClick }) => {
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLandingPage, setEditingLandingPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userLimits, setUserLimits] = useState({ maxLandingPages: 0 });
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Modal states handled by parent App.js

  useEffect(() => {
    fetchLandingPages();
  }, [projectId, currentPage]);

  // Only fetch user limits once on mount
  useEffect(() => {
    fetchUserLimits();
  }, []);

  const fetchUserLimits = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        const limits = {
          maxLandingPages: userData.user?.subscription?.maxLandingPages || 0
        };
        setUserLimits(limits);
      }
    } catch (error) {
      console.error('Error fetching user limits:', error);
    }
  };

  const fetchLandingPages = async () => {
    try {
      setLoading(true);
      const url = projectId 
        ? `/api/landing-pages?projectId=${projectId}&page=${currentPage}&limit=12`
        : `/api/landing-pages?page=${currentPage}&limit=12`;
        
      console.log('Fetching landing pages from:', url);
      console.log('Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error('Failed to fetch landing pages');
      }

      const data = await response.json();
      console.log('Landing pages data:', data);
      console.log('Landing pages:', data.landingPages);
      console.log('Total:', data.total);
      setLandingPages(data.landingPages);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      toast.error('Failed to fetch landing pages');
    } finally {
      setLoading(false);
    }
  };

  const canCreateLandingPage = useMemo(() => {
    const canCreate = userLimits.maxLandingPages === -1 || total < userLimits.maxLandingPages;
    return canCreate;
  }, [userLimits.maxLandingPages, total]);

  // Modal handlers handled by parent App.js

  const handleCreate = useCallback(() => {
    // Check if user has reached their limit
    if (!canCreateLandingPage) {
      // Open upgrade modal
      if (onUpgradeClick) {
        onUpgradeClick('landing-pages');
      }
      return;
    }
    
    setEditingLandingPage(null);
    setShowForm(true);
  }, [canCreateLandingPage, onUpgradeClick]);

  const handleEdit = (landingPage) => {
    setEditingLandingPage(landingPage);
    setShowForm(true);
  };

  const handleDelete = async (landingPageId) => {
    try {
      const response = await fetch(`/api/landing-pages/${landingPageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete landing page');
      }

      toast.success('Landing page deleted successfully');
    setLandingPages(prev => prev.filter(lp => lp._id !== landingPageId));
    setTotal(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting landing page:', error);
      toast.error('Failed to delete landing page');
    }
  };

  const handleView = (landingPage) => {
    console.log('handleView called with:', landingPage);
    const url = landingPage.customUrl || landingPage.title?.toLowerCase().replace(/[^a-z0-9]/g, '-');
    console.log('Opening URL:', `/landing/${url}`);
    // Open in new tab
    window.open(`/landing/${url}`, '_blank');
  };

  const handleCopy = (landingPage) => {
    const url = landingPage.customUrl || landingPage.title?.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const fullUrl = `${window.location.origin}/landing/${url}`;
    
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success('Landing page URL copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Landing page URL copied to clipboard!');
    });
  };

  const handleFormSave = (formData) => {
    setShowForm(false);
    setEditingLandingPage(null);
    fetchLandingPages();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingLandingPage(null);
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-black py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <button
              onClick={handleFormCancel}
              className="flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Landing Pages
            </button>
          </div>
          <LandingPageForm
            isOpen={showForm}
            onClose={handleFormCancel}
            onSubmit={handleFormSave}
            project={{ _id: projectId }}
            initialData={editingLandingPage}
            isLoading={false}
            onDelete={handleDelete}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6">
        {/* Analytics Cards */}
        <LandingPageAnalytics 
          total={total}
          published={landingPages.filter(lp => lp.status === 'published').length}
          drafts={landingPages.filter(lp => lp.status === 'draft').length}
          userLimits={userLimits}
        />

        {/* Landing Pages Section */}
        <div className="bg-black rounded-lg border border-gray-800">
          <div className="p-6">
            <LandingPageHeader 
              canCreateLandingPage={canCreateLandingPage}
              onCreate={handleCreate}
              onShowVideo={() => setShowVideoModal(true)}
              total={total}
            />

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : landingPages.length === 0 ? (
              <LandingPageEmptyState 
                canCreateLandingPage={canCreateLandingPage}
                onCreate={handleCreate}
              />
            ) : (
              <LandingPageListItems 
                landingPages={landingPages}
                onView={handleView}
                onCopy={handleCopy}
                onEdit={handleEdit}
              />
            )}

            <LandingPagePagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/w_YE2Uvd98I"
        title="How to Create a Landing Page?"
      />

          {/* Modals handled by parent App.js */}
        </>
  );
};

export default LandingPageList;
